import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import pluralize from "pluralize";

import { Alert, Box, Chip, Typography, useTheme } from "@mui/material";
import {
  RiCheckboxCircleFill,
  RiErrorWarningLine,
  RiInformationLine,
} from "@remixicon/react";

import { ShippingDialogProps } from "../../../types/Shipping";
import BaseShippingDialog from "./BaseShippingDialog";
import {
  isReadyForChecksum,
  isWithinDeliveryTimeframe,
} from "../../../utils/shippingManagementValidators";
import { useDataPlorApi } from "../../../hooks/useDataplorApi";

const ChecksumDialog: React.FC<ShippingDialogProps> = (props) => {
  const theme = useTheme();
  const { postDataplorApi } = useDataPlorApi();

  const { selectedData } = props;
  const {
    fullySelectedGroups,
    partiallySelectedGroups,
    totalSelectedShipments,
  } = selectedData;

  const [isComplete, setIsComplete] = useState(false);

  const selectedShipments = [
    ...fullySelectedGroups.flatMap((group) => group.shipments),
    ...partiallySelectedGroups.flatMap((group) => group.shipments),
  ];

  const uniqueShipmentIds = Array.from(
    new Set(
      selectedShipments
        .map((shipment) => shipment.fgs_id)
        .filter((id): id is number => typeof id === "number")
    )
  );

  // check if all shipments are successfully shipped
  const allReadyForChecksum = selectedShipments.every((shipment) =>
    isReadyForChecksum(shipment)
  );

  // check if all shipments are within the 12-hour timeframe
  const allWithinTimeframe = isWithinDeliveryTimeframe(selectedShipments);

  // shipments that not successfully shipped for user feedback
  const nonSuccessfulShipments = selectedShipments.filter(
    (shipment) => !isReadyForChecksum(shipment)
  );

  const {
    mutateAsync: exportShipment,
    isPending,
    isSuccess,
    isError,
  } = useMutation({
    mutationFn: async () => {
      const fgsIds = [
        ...new Set(selectedShipments.map((shipment) => shipment.fgs_id)),
      ];

      try {
        await postDataplorApi(`/v3/admin/apple_checksums`, {
          data: {
            attributes: {
              customer_id: 1,
              fulfillment_group_shipment_ids: fgsIds,
            },
          },
        });
      } catch (error) {
        console.error(error);
        throw error;
      }

      return true;
    },
    onSuccess: () => {
      setIsComplete(true);
    },
    onError: (e) => {
      console.error(e);
    },
  });

  const handleSubmit = () => {
    exportShipment();
  };

  const isSubmitDisabled =
    isPending ||
    isComplete ||
    selectedShipments.length === 0 ||
    !allReadyForChecksum ||
    !allWithinTimeframe;

  return (
    <BaseShippingDialog
      {...props}
      title="Checksum"
      onSubmit={handleSubmit}
      submitText={
        isPending
          ? `Delivering ${pluralize("checksum", selectedShipments.length)}...`
          : `Deliver ${pluralize("checksum", selectedShipments.length)}`
      }
      submitDisabled={isSubmitDisabled}
    >
      <Box>
        {selectedShipments.length === 0 && uniqueShipmentIds.length === 0 && (
          <Alert
            severity="info"
            sx={{ mb: 2, display: "flex", gap: 1 }}
            icon={false}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <RiInformationLine
                size={20}
                style={{ color: theme.palette.info.main }}
              />
              <Typography variant="body2">
                Please select at least one delivery with a shipment id to
                proceed.
              </Typography>
            </Box>
          </Alert>
        )}

        {selectedShipments.length > 0 && !allReadyForChecksum && (
          <>
            <Alert
              severity="error"
              sx={{ mb: 2, display: "flex", gap: 1 }}
              icon={false}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <RiErrorWarningLine
                  size={20}
                  style={{ color: theme.palette.error.main }}
                />
                <Typography variant="body2">
                  Cannot deliver{" "}
                  {pluralize("checksum", nonSuccessfulShipments.length)}:{" "}
                  {nonSuccessfulShipments.length}{" "}
                  {pluralize("shipment", nonSuccessfulShipments.length)}
                  not successfully shipped.
                </Typography>
              </Box>
            </Alert>
            <Box
              sx={{ mt: 2, mb: 0, gap: 1, display: "flex", flexWrap: "wrap" }}
            >
              {nonSuccessfulShipments.map((shipment) => (
                <Chip
                  key={`status-${shipment.delivery_id}`}
                  label={`${shipment.delivery_name} (${
                    shipment.fulfillment_status || "Unknown"
                  })`}
                />
              ))}
            </Box>
          </>
        )}

        {selectedShipments.length > 0 &&
          allReadyForChecksum &&
          !allWithinTimeframe && (
            <Alert
              severity="error"
              sx={{ mb: 2, display: "flex", gap: 1 }}
              icon={false}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <RiErrorWarningLine
                  size={20}
                  style={{ color: theme.palette.error.main }}
                />
                <Typography variant="body2">
                  Cannot process: Checksum must be created within 12 hours of
                  the first successful delivery.
                </Typography>
              </Box>
            </Alert>
          )}

        {selectedShipments.length > 0 &&
          allReadyForChecksum &&
          allWithinTimeframe && (
            <Box>
              <Typography sx={{ mb: 2 }}>
                Are you sure you want to process {totalSelectedShipments}{" "}
                {pluralize("delivery", totalSelectedShipments)}?
              </Typography>

              {isSuccess && (
                <Alert
                  severity="success"
                  sx={{ mt: 2, display: "flex", gap: 1 }}
                  icon={false}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <RiCheckboxCircleFill
                      size={20}
                      style={{ color: theme.palette.success.main }}
                    />
                    <Typography variant="body2">
                      Successfully processed all {selectedShipments.length}{" "}
                      {pluralize("item", selectedShipments.length)}.
                    </Typography>
                  </Box>
                </Alert>
              )}

              {isError && (
                <>
                  <Alert
                    severity="error"
                    sx={{ mt: 2, display: "flex", gap: 1 }}
                    icon={false}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <RiErrorWarningLine
                        size={20}
                        style={{ color: theme.palette.error.main }}
                      />
                      <Typography variant="body2">
                        Error delivering checksums occurred.
                      </Typography>
                    </Box>
                  </Alert>
                </>
              )}
            </Box>
          )}
      </Box>
    </BaseShippingDialog>
  );
};

export default ChecksumDialog;
